import React from 'react';
import { X, Loader2, ChevronUp, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { ReferenceModalProps } from './../types/types';

import { lightenColor } from "../constants/lightenColor";
import { isLightColor } from "../utils/LuminosityChecker";
import { darkenColor } from "../constants/darkenColor";

const ReferenceModal: React.FC<ReferenceModalProps> = ({
  isModalOpen,
  onClose,
  isLoadingChunks,
  reference,
  configData,
  onToggleChunk
}) => {
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const isLight = isLightColor(backgroundColor);

  const lighterInputBoxBackground = isLight
    ? darkenColor(backgroundColor, 7)
    : lightenColor(backgroundColor, 7);

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 z-50 ${
        isModalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div
        className={`absolute right-0 top-0 h-full w-[80%] lg:w-[40%] bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
          isModalOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Reference</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Panel Content */}
        <div className="p-6 max-h-[85vh] overflow-y-auto">
          <div className="mb-8">
            {isLoadingChunks ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : reference.length > 0 ? (
              <ul className="space-y-3 list-none">
                {reference.map((item, index) => (
                  <li key={index} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <div 
                      className="flex items-center justify-between bg-white hover:bg-gray-50 p-3 cursor-pointer transition-colors duration-150"
                      onClick={() => onToggleChunk(index)}
                    >
                      <div className="flex flex-col text-gray-800 gap-1 flex-wrap">
                        <div className="flex items-center text-xs text-black flex-wrap mx-1 gap-1">
                          <span className="font-bold text-sm">{item?.source} </span>
                          {item?.course_name && 
                            <>
                              <ChevronRight size={14} className="mx-1" />
                              <span className="italic break-all">{item?.course_name} </span>
                            </>
                          }
                          
                          {item?.source_category &&
                            <>
                              <ChevronRight size={14} className="mx-1" />
                              <span className="break-words">{item?.source_category}</span>
                            </>
                          }
                          {item?.file_name && item?.source_category === 'File' && 
                            <>
                              <ChevronRight size={14} className="mx-1" />
                              <span>{item?.file_name}</span>
                            </>
                          }
                          {item.url && 
                            <>
                              <ChevronRight size={14} className="mx-1" />
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span>Link</span>
                                <ExternalLink size={12} />
                              </a>
                            </>
                          }
                        </div>
                      </div>
                      <button 
                        className="text-gray-500 hover:text-blue-600 transition-colors duration-150"
                        aria-label={item.show ? "Hide content" : "Show content"}
                      >
                        {item.show ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </div>

                    {item.show && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4 m-0 overflow-x-auto whitespace-pre-wrap text-sm custom-scroll">
                        <span style={{ display: 'inline' }}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: item?.chunk
                                .replace(/\b([A-Z]{5,})\b/g, "<strong>$1</strong>") // FULL CAPS words
                                .replace(
                                  /\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g,
                                  '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">$1</a>'
                                ) // markdown [text](url)
                                .replace(
                                  /(https?:\/\/[^\s]+)/g,
                                  '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">Link</a>'
                                ) // raw URLs → LINK
                            }}
                          />
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No chunks found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferenceModal;